import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { Form, Select } from 'antd';
import { RevxRadio } from 'components';
import { RevxCheckbox, RevxCheckboxProps } from 'components/RevxCheckbox';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxSvcInputNumber } from 'components/RevxSvcInputNumber';
import { RevxRadioProps } from 'components/RevxRadio';
import {
  JobOptionSvcOptions,
  ServiceOptionTypeEnum,
} from 'domains/jobconfigDomain';
import { Fragment, useEffect, useMemo } from 'react';
import { ConfigSvcProps } from '..';
import { useSvcDisabled } from '../hook/useSvcDisabled';

const { Option } = Select;

interface CommonJobSvcOptionProps extends ConfigSvcProps {
  customerRadioProps?: (
    labelArr: string[],
    disabled?: boolean,
    label?: string
  ) => RevxRadioProps;
  customerCheckBoxProps?: (
    labelArr: string[],
    disabled?: boolean,
    label?: string
  ) => RevxCheckboxProps;
  customerOptions?: JobOptionSvcOptions[];
  optionLabelMarginBottom?: number;
}

const CommonJobSvcOption = (props: CommonJobSvcOptionProps) => {
  const { optionValue } = useMeasurementUnit();
  const {
    jobOptionSvc,
    id,
    customerRadioProps,
    customerCheckboxProps,
    customerOptions,
    optionLabelMarginBottom = 0,
  } = props;

  const svcDisabled = useSvcDisabled(props);

  // Set defaultValue for INPUT options if form value is not set
  useEffect(() => {
    const options = customerOptions || jobOptionSvc?.options;
    options?.forEach((option) => {
      if (option.type === ServiceOptionTypeEnum.INPUT && option.defaultValue) {
        const currentVal = props.form?.getFieldValue([id, option.label]);
        if (currentVal === undefined || currentVal === null || currentVal === '') {
          props.form?.setFieldsValue({
            [id]: { ...props.form?.getFieldValue(id), [option.label]: option.defaultValue },
          });
          if (props.formCache && props.formCache[id]) {
            props.formCache[id][option.label] = option.defaultValue;
          }
        }
      }
    });
  }, [customerOptions, jobOptionSvc?.options, id, props.form, props.formCache]);

  const attrRadioProps = (
    labelArr: string[],
    valuesMetric?: Record<string, string> | null
  ): RevxRadioProps => {
    return {
      disabled: svcDisabled,
      outSideRevxLayoutProps: {
        align: 'top',
        justify: 'left',
        wrap: 'wrap',
        className: 'jobconfig-jobOption-content-outSideOption-default',
        style: {
          paddingBottom: 0,
          marginTop: 8,
        },
      },
      inSideRevxLayoutProps: {
        align: 'top',
        justify: 'left',
        className: 'jobconfig-jobOption-content-inSideOption-default',
        style: {
          marginRight: 31,
        },
      },
      options:
        labelArr?.map((label) => {
          return {
            // value stays the supplier's own string - it is what gets submitted and matched
            value: label,
            children: <span>{optionValue(label, valuesMetric)}</span>,
          };
        }) || [],
    };
  };

  const optionRadioRender = (
    labelArr: string[],
    label?: string,
    valuesMetric?: Record<string, string> | null
  ): RevxRadioProps => {
    if (typeof customerRadioProps === 'function') {
      return customerRadioProps(labelArr, svcDisabled, label);
    } else {
      return attrRadioProps(labelArr, valuesMetric);
    }
  };

  const attrCheckboxProps = (
    labelArr: string[],
    valuesMetric?: Record<string, string> | null
  ): RevxCheckboxProps => {
    return {
      disabled: svcDisabled,
      outSideRevxLayoutProps: {
        align: 'top',
        justify: 'left',
        wrap: 'wrap',
        className: 'jobconfig-jobOption-content-outSideOption-default',
        style: {
          paddingBottom: 0,
        },
      },
      inSideRevxLayoutProps: {
        align: 'top',
        justify: 'left',
        className: 'jobconfig-jobOption-content-inSideOption-default',
        style: {
          marginRight: 31,
        },
      },
      options:
        labelArr?.map((label) => {
          return {
            // value stays the supplier's own string - it is what gets submitted and matched
            value: label,
            children: <span>{optionValue(label, valuesMetric)}</span>,
          };
        }) || [],
    };
  };

  const optionCheckboxRender = (
    labelArr: string[],
    label?: string,
    valuesMetric?: Record<string, string> | null
  ): RevxCheckboxProps => {
    if (typeof customerCheckboxProps === 'function') {
      return customerCheckboxProps(labelArr, svcDisabled, label);
    } else {
      return attrCheckboxProps(labelArr, valuesMetric);
    }
  };

  const optionLabelStyle = useMemo(() => {
    return { margin: `0 10px ${optionLabelMarginBottom}px 0` };
  }, [optionLabelMarginBottom]);

  return (
    <>
      {(customerOptions || jobOptionSvc?.options)?.map((option, idx) => {
        if (option.type === ServiceOptionTypeEnum.RADIO) {
          return (
            <Fragment key={`${option.label}${idx}option`}>
              <div
                className="jobconfig-jobOption-container"
                // style={{ position: 'relative', top: -20 }}
              >
                <div
                  className="jobconfig-jobOption-content-label"
                  style={optionLabelStyle}
                >
                  {option.label}
                </div>
                {option.label.toLowerCase() === 'color' ? (
                  <Form.Item name={[id, option.label]} noStyle>
                    <Select
                      className="jobconfig-jobOption-select"
                      disabled={svcDisabled}
                      getPopupContainer={(e) => {
                        return e as HTMLElement;
                      }}
                      style={{
                        marginTop: '6px',
                        minWidth: '100px',
                      }}
                      virtual={false}
                    >
                      {option?.values?.map((value) => (
                        <Option value={value} key={`${value}${option.label}`}>
                          {optionValue(value, option.valuesMetric)}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : (
                  <Form.Item name={[id, option.label]} noStyle>
                    <RevxRadio
                      {...optionRadioRender(option.values, option.label, option.valuesMetric)}
                    />
                  </Form.Item>
                )}
              </div>
            </Fragment>
          );
        } else if (option.type === ServiceOptionTypeEnum.INPUT) {
          return (
            <RevxFlexLayout
              align={'center'}
              // style={{ margin: '0px 4px 8px 0' }}
              key={`${option.label}${idx}option`}
            >
              <div
                className="jobconfig-jobOption-content-label"
                style={optionLabelStyle}
              >
                {option.label}:
              </div>
              <Form.Item name={[id, option.label]} noStyle>
                <RevxSvcInputNumber
                  allowLeadingDecimal
                  triggerOnBlur
                  disabled={svcDisabled}
                  min={0}
                  max={999}
                  style={{ marginRight: 10, width: 120, height: 28 }}
                />
              </Form.Item>
            </RevxFlexLayout>
          );
        } else if (option.type === ServiceOptionTypeEnum.CHECKBOX) {
          return (
            <Fragment key={`${option.label}${idx}option`}>
              <div
              // style={{ margin: '0 0 14px 0' }}
              >
                <div
                  className="jobconfig-jobOption-content-label"
                  style={optionLabelStyle}
                >
                  {option.label}
                </div>
                {option.label.toLowerCase() === 'color' ? (
                  <Form.Item name={[id, option.label]} noStyle>
                    <Select
                      className="jobconfig-jobOption-select"
                      disabled={svcDisabled}
                      getPopupContainer={(e) => {
                        return e as HTMLElement;
                      }}
                      virtual={false}
                    >
                      {option?.values?.map((value) => (
                        <Option value={value} key={`${value}${option.label}`}>
                          {value}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : (
                  <Form.Item name={[id, option.label]} noStyle>
                    <RevxCheckbox
                      {...optionCheckboxRender(option.values, option.label, option.valuesMetric)}
                    />
                  </Form.Item>
                )}
              </div>
            </Fragment>
          );
        } else {
          return <Fragment key={`${option.label}${idx}option`}></Fragment>;
        }
      })}
    </>
  );
};

export default CommonJobSvcOption;
